import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import api from '../../api/client';
import Donut from '../common/Donut';

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DAY_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAY_COLORS = ['#ef4444', '#7c3aed', '#06b6d4', '#16a34a', '#f59e0b', '#ec4899', '#3b82f6'];
const PROGRAM_COLORS = ['#8b5cf6', '#06b6d4', '#16a34a', '#f59e0b', '#ec4899', '#3b82f6', '#ef4444', '#22d3ee', '#a3e635', '#f97316'];
const DAYTYPE_COLORS = { weekday: '#3b82f6', weekend: '#ec4899' };
const DAYTYPE_LABELS = { weekday: 'Lunes–Viernes', weekend: 'Sábado–Domingo' };

export default function Stats() {
  const [overview, setOverview] = useState(null);
  const [topSongs, setTopSongs] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [daytype, setDaytype] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [trend, setTrend] = useState(null);
  const [airing, setAiring] = useState([]);
  const [records, setRecords] = useState(null);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);

  const fetchAll = async (d = days) => {
    setLoading(true);
    try {
      const [o, t, c, ref, dt, hm, tr, ar, rc] = await Promise.all([
        api.get('/api/stats/overview', { params: { days: d } }),
        api.get('/api/stats/top-songs', { params: { limit: 15 } }),
        api.get('/api/stats/show-coverage'),
        api.get('/api/stats/referrers', { params: { days: d } }).catch(() => ({ data: { referrers: [], user_agents: [] } })),
        api.get('/api/stats/daytype', { params: { days: d } }).catch(() => ({ data: { daytype: [], daytype_by_hour: [] } })),
        api.get('/api/stats/heatmap', { params: { days: d } }).catch(() => ({ data: { cells: [] } })),
        api.get('/api/stats/trend', { params: { days: d } }).catch(() => ({ data: { series: [] } })),
        api.get('/api/stats/airing-programs', { params: { days: d } }).catch(() => ({ data: [] })),
        api.get('/api/stats/records').catch(() => null),
      ]);
      setOverview(o.data);
      setTopSongs(t.data || []);
      setCoverage(c.data || []);
      setReferrers(ref.data?.referrers || []);
      setDaytype(dt.data);
      setHeatmap(hm.data);
      setTrend(tr.data);
      setAiring(ar.data || []);
      setRecords(rc.data);
    } catch (err) {
      console.error('[Stats] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll(14);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeDays = (d) => {
    setDays(d);
    fetchAll(d);
  };

  if (loading && !overview) {
    return <div className="py-12 text-center text-dark-400">Cargando estadísticas...</div>;
  }

  const k = overview?.kpis || {};
  const maxHour = Math.max(1, ...(overview?.by_hour || []).map((h) => parseFloat(h.avg_listeners) || 0));
  const maxSong = Math.max(1, ...topSongs.map((s) => parseInt(s.plays, 10) || 0));
  const maxDay = Math.max(1, ...(overview?.by_day || []).map((d) => parseFloat(d.avg_listeners) || 0));
  const programs = overview?.programs || [];
  const maxProg = Math.max(1, ...programs.map((p) => parseFloat(p.avg_listeners) || 0));
  const withoutMaterial = coverage.filter((s) => s.sin_material);

  const hmCells = heatmap?.cells || [];
  const hmMax = Math.max(0.1, ...hmCells.map((c) => c.avg_listeners || 0));
  const hmBest = hmCells.length ? hmCells.reduce((a, b) => ((b.avg_listeners || 0) > (a.avg_listeners || 0) ? b : a)) : null;
  const trendSeries = trend?.series || [];
  const maxAir = Math.max(1, ...airing.map((a) => a.avg_listeners || 0));

  const dowDonut = (overview?.by_day || []).map((d) => ({
    label: DAY_NAMES[d.dow],
    value: Math.round(parseFloat(d.avg_listeners) || 0),
    color: DAY_COLORS[d.dow] || '#94a3b8',
  }));
  const bak = overview?.by_day || [];

  const kpiCards = [
    { label: 'Promedio oyentes (ult. 7 días)', value: k.avg_7d, icon: '👂', accent: 'text-primary-400' },
    { label: 'Promedio (ult. 30 días)', value: k.avg_30d, icon: '📈', accent: 'text-secondary-400' },
    { label: 'Pico de oyentes (ult. 7 días)', value: k.peak_7d, icon: '🚀', accent: 'text-warning-400' },
    { label: 'Hoy (promedio)', value: k.avg_today, icon: '☀️', accent: 'text-success-400' },
    { label: 'Pico hoy', value: k.peak_today, icon: '🎯', accent: 'text-error-400' },
    { label: 'Reproducciones (período)', value: k.total_plays, icon: '🎵', accent: 'text-purple-400' },
    { label: 'Programas en aire', value: k.programs_played, icon: '📻', accent: 'text-cyan-400' },
    { label: 'Horas de contenido emitido', value: k.hours_played ? `${k.hours_played}` : 0, icon: '⏱', accent: 'text-blue-400' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-dark-100">Estadísticas</h1>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-dark-700">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => changeDays(d)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${days === d ? 'bg-primary-600 text-white' : 'bg-dark-800 text-dark-400 hover:text-dark-200'}`}
              >
                {d} días
              </button>
            ))}
          </div>
          <button onClick={() => fetchAll()} className="btn btn-secondary btn-sm" title="Actualizar">↻</button>
        </div>
      </div>

      {/* KPIs numéricos */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {kpiCards.map((c, i) => (
          <div key={i} className="rounded-xl bg-dark-850 border border-dark-700 p-4">
            <div className="flex items-center gap-2">
              <span className="text-base">{c.icon}</span>
              <span className="text-[11px] text-dark-400">{c.label}</span>
            </div>
            <p className={`mt-1 text-2xl font-bold ${c.accent}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Dónut días + barras por hora */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">🗓 Oyentes por día de la semana (promedio)</h2>
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <Donut
              data={dowDonut}
              centerLabel={String(k.avg_7d ?? 0)}
              centerSub="promedio"
            />
            <div className="w-full sm:w-auto flex-1 space-y-1.5">
              {bak.map((d) => (
                <div key={d.dow} className="flex items-center gap-2">
                  <span className="w-9 text-[11px] font-medium text-dark-400">{DAY_NAMES[d.dow]}</span>
                  <div className="flex-1 h-2 rounded bg-dark-800 overflow-hidden">
                    <div
                      className="h-full rounded"
                      style={{ width: `${((parseFloat(d.avg_listeners) || 0) / maxDay) * 100}%`, backgroundColor: DAY_COLORS[d.dow] }}
                    />
                  </div>
                  <span className="w-6 text-right text-xs text-dark-300">{Math.round(parseFloat(d.avg_listeners) || 0)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">👂 Oyentes por hora (promedio)</h2>
          {!overview?.by_hour?.length ? (
            <p className="text-dark-400">Todavía no hay datos de oyentes.</p>
          ) : (
            <div className="flex h-48 items-end gap-0.5">
              {overview.by_hour.map((h) => {
                const v = parseFloat(h.avg_listeners) || 0;
                const isPeak = v === maxHour;
                return (
                  <div key={h.hour} className="group relative flex flex-1 flex-col items-center gap-1" title={`${h.hour}:00 → ${v} oyentes (pico ${h.peak})`}>
                    <div className="flex w-full flex-1 items-end">
                      <div
                        className={`w-full rounded-t ${isPeak ? 'bg-primary-500' : 'bg-primary-600/60'} ${isPeak ? 'shadow-[0_0_8px_rgba(139,92,246,0.8)]' : ''}`}
                        style={{ height: `${Math.max(3, (v / maxHour) * 100)}%` }}
                      />
                    </div>
                    {h.hour % 3 === 0 && <span className="text-[9px] text-dark-500">{h.hour}</span>}
                    <span className="pointer-events-none absolute -top-6 hidden rounded bg-dark-900 px-1.5 py-0.5 text-[10px] text-white group-hover:block">
                      {h.hour}:00 · {v}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mapa de calor: días y horarios de mayor audiencia */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
          <h2 className="text-lg font-semibold text-dark-100">🔥 Días y horarios de mayor audiencia</h2>
          {hmBest && (
            <span className="text-xs text-dark-400">
              Mejor franja: <span className="font-semibold text-success-400">{DAY_FULL[hmBest.dow]} {hmBest.hour}:00</span> · promedio {hmBest.avg_listeners} oyentes (pico {hmBest.peak_listeners})
            </span>
          )}
        </div>
        <p className="text-xs text-dark-500 mb-4">Promedio de oyentes por día de la semana y hora (hora de Argentina). Más intenso = más oyentes escuchando.</p>
        {hmCells.length === 0 ? (
          <p className="text-dark-400">Sin datos suficientes para el mapa de calor todavía.</p>
        ) : (
          <div className="overflow-x-auto pb-2">
            <div className="min-w-[760px]">
              <div className="flex items-center gap-2">
                <span className="w-10" />
                <div className="grid flex-1 gap-0.5" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <span key={h} className="text-center text-[8px] text-dark-500">{h % 3 === 0 ? `${h}h` : ''}</span>
                  ))}
                </div>
              </div>
              {DAY_NAMES.map((dn, dow) => (
                <div key={dow} className="mt-0.5 flex items-center gap-2">
                  <span className="w-10 text-[10px] font-medium text-dark-400">{dn}</span>
                  <div className="grid flex-1 gap-0.5" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                    {Array.from({ length: 24 }, (_, h) => {
                      const cell = hmCells.find((c) => c.dow === dow && c.hour === h);
                      const v = cell ? cell.avg_listeners : null;
                      const intensity = v != null ? Math.max(0.08, (v / hmMax) * 0.92 + 0.08) : 0;
                      const isBest = hmBest && cell && hmBest.dow === dow && hmBest.hour === h;
                      return (
                        <div
                          key={h}
                          className={`h-6 rounded-sm ${isBest ? 'ring-1 ring-success-400' : ''}`}
                          style={{ backgroundColor: v != null ? `rgba(139, 92, 246, ${intensity})` : 'rgba(30, 35, 50, 0.5)' }}
                          title={cell
                            ? `${DAY_FULL[dow]} ${h}:00 → ${cell.avg_listeners} oyentes promedio (pico ${cell.peak_listeners}, ${cell.samples} muestras)`
                            : `${DAY_FULL[dow]} ${h}:00 → sin datos`}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="mt-3 flex items-center gap-2 text-[10px] text-dark-500">
                <span>Menos</span>
                {[0.15, 0.35, 0.55, 0.75, 1].map((a) => (
                  <span key={a} className="h-3 w-6 rounded-sm" style={{ backgroundColor: `rgba(139, 92, 246, ${a})` }} />
                ))}
                <span>Más oyentes</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Récords históricos con fechas reales */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
        <h2 className="text-lg font-semibold text-dark-100 mb-1">🏆 Récords históricos (fechas reales)</h2>
        <p className="text-xs text-dark-500 mb-4">
          Todo el histórico disponible{records?.since ? ` desde el ${records.since}` : ''}. Horas de escucha = consumo real (1 oyente durante 1 hora = 1 hora).
        </p>
        {!records ? (
          <p className="text-dark-400">Cargando récords históricos…</p>
        ) : (() => {
          const ap = records.absolute_peak || {};
          const lh = records.listener_hours || {};
          const mom = records.top_moments || [];
          const dys = records.top_days || [];
          const maxDayRec = Math.max(1, ...dys.map((d) => d.avg_listeners || 0));
          return (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 mb-6">
                <div className="rounded-lg bg-dark-800/50 border border-dark-700 p-3">
                  <span className="text-[11px] text-dark-400">Pico histórico (app)</span>
                  <p className="text-2xl font-bold text-error-400">{ap.value ?? '—'}</p>
                  <p className="text-[10px] text-dark-500">{ap.at_local ? `${ap.at_local} hs` : ''}</p>
                </div>
                <div className="rounded-lg bg-dark-800/50 border border-dark-700 p-3">
                  <span className="text-[11px] text-dark-400">Horas de escucha totales</span>
                  <p className="text-2xl font-bold text-primary-400">{lh.total ?? '—'}<span className="text-xs font-normal text-dark-400"> h</span></p>
                  <p className="text-[10px] text-dark-500">consumo acumulado</p>
                </div>
                <div className="rounded-lg bg-dark-800/50 border border-dark-700 p-3">
                  <span className="text-[11px] text-dark-400">Horas de escucha por día</span>
                  <p className="text-2xl font-bold text-secondary-400">{lh.per_day ?? '—'}<span className="text-xs font-normal text-dark-400"> h</span></p>
                  <p className="text-[10px] text-dark-500">promedio histórico</p>
                </div>
                <div className="rounded-lg bg-dark-800/50 border border-dark-700 p-3">
                  <span className="text-[11px] text-dark-400">Días con emisión</span>
                  <p className="text-2xl font-bold text-success-400">{lh.days_covered ?? '—'}</p>
                  <p className="text-[10px] text-dark-500">con datos de audiencia</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                  <h3 className="text-sm font-semibold text-dark-200 mb-2">🔥 Mejores momentos (fecha y hora exactas)</h3>
                  {mom.length === 0 ? (
                    <p className="text-dark-400">Sin datos aún.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Día</th>
                            <th>Fecha</th>
                            <th>Hora</th>
                            <th>Prom.</th>
                            <th>Pico</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mom.map((m, i) => (
                            <tr key={i}>
                              <td className="text-dark-500">{i + 1}</td>
                              <td className="text-dark-300">{DAY_NAMES[m.dow] || '—'}</td>
                              <td className="text-dark-300">{m.date ? `${m.date.slice(8, 10)}/${m.date.slice(5, 7)}` : '—'}</td>
                              <td className="text-dark-300">{m.hour}:00</td>
                              <td className="font-semibold text-dark-100">{m.avg_listeners}</td>
                              <td className="text-success-400">{m.peak_listeners}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-dark-200 mb-2">📅 Mejores días completos</h3>
                  {dys.length === 0 ? (
                    <p className="text-dark-400">Sin datos aún.</p>
                  ) : (
                    <div className="space-y-2">
                      {dys.map((d, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="w-6 text-xs text-dark-500 text-right">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-sm font-medium text-dark-100">
                                {DAY_NAMES[d.dow] || ''} {d.date ? `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}` : ''}
                              </span>
                              <span className="shrink-0 text-xs font-semibold text-primary-300">
                                {d.avg_listeners} oy. · pico {d.peak_listeners}
                              </span>
                            </div>
                            <div className="mt-1 h-2 rounded bg-dark-800 overflow-hidden">
                              <div className="h-full rounded bg-primary-600" style={{ width: `${(d.avg_listeners / maxDayRec) * 100}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* Evolución diaria de oyentes */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
        <h2 className="text-lg font-semibold text-dark-100 mb-1">📈 Evolución diaria de oyentes</h2>
        <p className="text-xs text-dark-500 mb-4">Promedio por día (línea) y pico diario (puntos). Combina oyentes de la app (TecnoSoul) y de la web.</p>
        {trendSeries.length < 2 ? (
          <p className="text-dark-400">Sin datos suficientes para la evolución diaria todavía.</p>
        ) : (() => {
          const W = 720, H = 190, P = 26;
          const maxV = Math.max(1, ...trendSeries.map((s) => s.peak_listeners || 0));
          const step = (W - P * 2) / (trendSeries.length - 1);
          const pts = trendSeries.map((s, i) => [P + i * step, H - P - ((s.avg_listeners || 0) / maxV) * (H - P * 2)]);
          const line = pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
          const area = `${P},${H - P} ${line} ${W - P},${H - P}`;
          const labelEvery = Math.max(1, Math.ceil(trendSeries.length / 9));
          return (
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
              <polyline points={area} fill="rgba(139,92,246,0.12)" stroke="none" />
              <polyline points={line} fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinejoin="round" />
              {trendSeries.map((s, i) => (
                <circle
                  key={i}
                  cx={pts[i][0]}
                  cy={H - P - ((s.peak_listeners || 0) / maxV) * (H - P * 2)}
                  r="3.2"
                  fill={s.peak_listeners === maxV ? '#22d3ee' : '#3b4b6b'}
                >
                  <title>{`${s.day} → promedio ${s.avg_listeners} oyentes · pico ${s.peak_listeners}`}</title>
                </circle>
              ))}
              {trendSeries.map((s, i) =>
                i % labelEvery === 0 || i === trendSeries.length - 1 ? (
                  <text key={`t${i}`} x={pts[i][0]} y={H - 6} textAnchor="middle" fontSize="9" fill="#64748b">{String(s.day).slice(5, 10)}</text>
                ) : null
              )}
            </svg>
          );
        })()}
      </div>

      {/* Programas más escuchados según emisión real */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
        <h2 className="text-lg font-semibold text-dark-100 mb-1">🏆 Programas más escuchados (emisión real)</h2>
        <p className="text-xs text-dark-500 mb-4">
          Audiencia promedio durante lo que realmente salió al aire en los últimos {days} días. Excluye publicidad e identificaciones.
        </p>
        {airing.length === 0 ? (
          <p className="text-dark-400">Sin datos de emisiones todavía.</p>
        ) : (
          <div className="space-y-2">
            {airing.map((a, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-6 text-xs text-dark-500 text-right">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium text-dark-100" title={a.program}>{a.program}</span>
                    <span className="shrink-0 text-xs font-semibold" style={{ color: PROGRAM_COLORS[i % PROGRAM_COLORS.length] }}>
                      {a.avg_listeners} oy. · pico {a.peak_listeners}
                    </span>
                  </div>
                  <p className="text-[11px] text-dark-500">{a.plays} emisiones · {a.hours_on_air} h al aire</p>
                  <div className="mt-1 h-2 rounded bg-dark-800 overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(a.avg_listeners / maxAir) * 100}%`, backgroundColor: PROGRAM_COLORS[i % PROGRAM_COLORS.length] }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Programas más escuchados */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
        <h2 className="text-lg font-semibold text-dark-100 mb-1">📻 Programas con más audiencia en su franja horaria (schedule)</h2>
        <p className="text-xs text-dark-500 mb-4">
          Promedio de oyentes durante la franja horaria de cada programa en los últimos {days} días.
        </p>
        {programs.length === 0 ? (
          <p className="text-dark-400">No hay datos suficientes todavía.</p>
        ) : (
          <div className="space-y-2">
            {programs.slice(0, 12).map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-10 text-[10px] text-dark-500">
                  {p.day_pattern === 'weekdays' ? 'L-V' : p.day_pattern === 'saturday' ? 'Sáb' : p.day_pattern === 'sunday' ? 'Dom' : 'Esp'}
                </span>
                <div className="w-20 shrink-0 text-[11px] text-dark-400 text-right">{p.start_time}–{p.end_time}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium text-dark-100" title={`${p.name}${p.dj_name ? ' · ' + p.dj_name : ''}`}>
                      {p.name}
                    </span>
                    <span className="shrink-0 text-xs font-semibold" style={{ color: PROGRAM_COLORS[i % PROGRAM_COLORS.length] }}>
                      {parseFloat(p.avg_listeners).toFixed(1)} oy.
                    </span>
                  </div>
                  {p.dj_name && <p className="truncate text-[11px] text-dark-500">{p.dj_name}</p>}
                  <div className="mt-1 h-2 rounded bg-dark-800 overflow-hidden">
                    <div
                      className="h-full rounded"
                      style={{ width: `${(parseFloat(p.avg_listeners) || 0) / maxProg * 100}%`, backgroundColor: PROGRAM_COLORS[i % PROGRAM_COLORS.length] }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Referrers / Fuentes de escucha */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
        <h2 className="text-lg font-semibold text-dark-100 mb-4">🔗 Fuentes de escucha (dónde dieron play)</h2>
        {referrers.length === 0 ? (
          <p className="text-dark-400">Sin datos de referrers todavía. Requiere credenciales admin Icecast TecnoSoul.</p>
        ) : (
          <div className="space-y-2">
            {referrers.slice(0, 10).map((r, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-6 text-xs text-dark-500 text-right">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium text-dark-100" title={r.referrer}>{r.referrer}</span>
                    <span className="shrink-0 text-xs font-semibold text-primary-300">{r.plays} ({r.pct}%)</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded bg-dark-700 overflow-hidden">
                    <div className="h-full bg-primary-600" style={{ width: `${r.pct}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Semana vs Fin de semana */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
        <h2 className="text-lg font-semibold text-dark-100 mb-4">📅 Semana vs Fin de semana</h2>
        {!daytype?.daytype?.length ? (
          <p className="text-dark-400">Sin datos para comparar semana vs fin de semana.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {daytype.daytype.map((dt) => (
                <div key={dt.day_type} className="rounded-lg bg-dark-800/50 p-4 border border-dark-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: DAYTYPE_COLORS[dt.day_type] }} />
                    <span className="font-semibold text-dark-100">{DAYTYPE_LABELS[dt.day_type] || dt.day_type}</span>
                  </div>
                  <div className="text-2xl font-bold" style={{ color: DAYTYPE_COLORS[dt.day_type] }}>{dt.avg_listeners}</div>
                  <div className="text-xs text-dark-400">Pico: {dt.peak_listeners} · Muestras: {dt.samples}</div>
                </div>
              ))}
            </div>
            <div className="rounded-lg bg-dark-800/50 p-4 border border-dark-700">
              <h3 className="text-sm font-medium text-dark-100 mb-2">Por hora: Semana vs Fin de semana</h3>
              <div className="h-40 flex items-end gap-1.5 overflow-x-auto pb-2">
                {(() => {
                  const hours = Array.from({ length: 24 }, (_, i) => i);
                  const wk = daytype.daytype_by_hour?.filter((d) => d.day_type === 'weekday') || [];
                  const we = daytype.daytype_by_hour?.filter((d) => d.day_type === 'weekend') || [];
                  const maxAll = Math.max(1, ...wk.map((d) => d.avg_listeners || 0), ...we.map((d) => d.avg_listeners || 0));
                  return hours.map((h) => {
                    const wd = wk.find((d) => d.hour === h);
                    const wee = we.find((d) => d.hour === h);
                    return (
                      <div key={h} className="group relative flex flex-1 flex-col items-center gap-1 min-w-[30px]" title={`${h}:00`}>
                        <div className="flex w-full flex-1 items-end gap-0.5">
                          {wd && (
                            <div
                              className="w-4 rounded-t"
                              style={{
                                height: `${Math.max(2, (wd.avg_listeners / maxAll) * 100)}%`,
                                backgroundColor: DAYTYPE_COLORS.weekday,
                              }}
                              title={`L-V ${h}:00 → ${wd.avg_listeners}`}
                            />
                          )}
                          {wee && (
                            <div
                              className="w-4 rounded-t"
                              style={{
                                height: `${Math.max(2, (wee.avg_listeners / maxAll) * 100)}%`,
                                backgroundColor: DAYTYPE_COLORS.weekend,
                              }}
                              title={`S-D ${h}:00 → ${wee.avg_listeners}`}
                            />
                          )}
                        </div>
                        <span className="text-[8px] text-dark-500">{h}</span>
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="flex gap-4 mt-2 text-xs text-dark-400">
                <span style={{ color: DAYTYPE_COLORS.weekday }}>■ Lunes–Viernes</span>
                <span style={{ color: DAYTYPE_COLORS.weekend }}>■ Sábado–Domingo</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Top canciones */}
        <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">🎵 Canciones más sonadas</h2>
          {topSongs.length === 0 ? (
            <p className="text-dark-400">Todavía no hay datos de reproducción.</p>
          ) : (
            <div className="space-y-2">
              {topSongs.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-6 text-xs text-dark-500 text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium text-dark-100" title={s.title}>{s.title}</span>
                      <span className="shrink-0 text-xs text-primary-300">{s.plays}×</span>
                    </div>
                    <p className="truncate text-[11px] text-dark-500">
                      {s.artist || '—'}{s.program_name ? ` · ${s.program_name}` : ''}
                    </p>
                    <div className="mt-1 h-1 rounded bg-dark-700 overflow-hidden">
                      <div className="h-full bg-primary-600" style={{ width: `${(s.plays / maxSong) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leyenda dónut días */}
        <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">🧭 Distribución semanal</h2>
          <p className="text-xs text-dark-500 mb-4">Qué porcentaje de la audiencia escuchó cada día de la semana.</p>
          <div className="space-y-2">
            {bak.length > 0 && (() => {
              const total = bak.reduce((a, d) => a + (parseFloat(d.avg_listeners) || 0), 0) || 1;
              return bak.map((d, i) => {
                const v = parseFloat(d.avg_listeners) || 0;
                const pct = Math.round((v / total) * 100);
                return (
                  <div key={d.dow} className="flex items-center gap-3">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: DAY_COLORS[d.dow] }} />
                    <span className="w-20 text-xs text-dark-300">{DAY_FULL[d.dow]}</span>
                    <div className="flex-1 h-2 rounded bg-dark-800 overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${pct}%`, backgroundColor: DAY_COLORS[d.dow] }} />
                    </div>
                    <span className="w-10 text-right text-xs text-dark-300">{pct}%</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Cobertura de horarios */}
      <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-dark-100">📋 Cobertura de horarios</h2>
          {withoutMaterial.length > 0 && (
            <span className="rounded bg-error-900/40 px-2 py-1 text-xs font-medium text-error-400">
              ⚠ {withoutMaterial.length} serie(s) sin material
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Serie</th>
                <th>Carpetas</th>
                <th>Archivos listos</th>
                <th>Set DJ</th>
                <th>Episodios</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((s) => (
                <tr key={s.id} className={s.sin_material ? 'bg-error-900/10' : ''}>
                  <td className="font-medium text-dark-100">{s.name}</td>
                  <td className="text-dark-300">{s.carpetas}</td>
                  <td className="text-dark-300">{s.archivos_listos}</td>
                  <td className="text-dark-300">{s.set_listo}</td>
                  <td className="text-dark-300">{s.episodios}</td>
                  <td>
                    {s.sin_material ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-error-900/40 text-error-400">Sin material</span>
                    ) : (
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-success-900/30 text-success-400">Con material</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-dark-500 mt-3">
          El sistema alerta automáticamente (🔔) si una serie activa queda sin material en sus carpetas, set o episodios.
        </p>
      </div>
    </div>
  );
}